import type { CameraProps } from '@vitals/shared';

export interface CameraPopup {
  show(cam: CameraProps, x: number, y: number): void;
  hide(): void;
}

const REFRESH_MS = 3000; // 511 stills update every few seconds

// Mirrors the mobile breakpoint in styles.css: below it, the popup is a
// CSS-positioned centered sheet, so the JS must not set inline left/top.
const MOBILE_QUERY = '(max-width: 640px), (max-width: 960px) and (max-height: 500px)';

/** A click-to-view popup showing a traffic camera's live still image. */
export function createCameraPopup(parent: HTMLElement): CameraPopup {
  let el: HTMLDivElement | null = null;
  let timer = 0;

  const hide = () => {
    if (timer) {
      clearInterval(timer);
      timer = 0;
    }
    el?.remove();
    el = null;
  };

  const show = (cam: CameraProps, x: number, y: number) => {
    hide();
    el = document.createElement('div');
    el.className = 'cam-popup';
    // On phones the popup is a centered sheet positioned entirely in CSS;
    // on larger screens, anchor it near the click and keep it on-screen.
    if (!window.matchMedia(MOBILE_QUERY).matches) {
      el.style.left = `${Math.min(x + 12, window.innerWidth - 340)}px`;
      el.style.top = `${Math.min(y + 12, window.innerHeight - 260)}px`;
    }

    const close = document.createElement('button');
    close.className = 'cam-close';
    close.type = 'button';
    close.setAttribute('aria-label', 'Close camera preview');
    close.textContent = '×';
    close.addEventListener('click', hide);

    const title = document.createElement('div');
    title.className = 'cam-title';
    title.textContent = cam.name; // textContent → XSS-safe

    const img = document.createElement('img');
    img.className = 'cam-img';
    img.alt = cam.name;
    img.referrerPolicy = 'no-referrer';

    el.append(close, title, img);
    parent.appendChild(el);

    const refresh = () => {
      img.src = `${cam.url}${cam.url.includes('?') ? '&' : '?'}t=${Date.now()}`;
    };
    refresh();
    timer = window.setInterval(refresh, REFRESH_MS);
  };

  return { show, hide };
}
